import { useState, FormEvent } from 'react';
import { AGENCY_INFO } from '../data/content';
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, Copy, Check, MessageCircle } from 'lucide-react';
import { validatePhone } from '../utils/phone';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null);

  const officialEmails = [
    { label: 'General & Prensa', email: 'contacto@epicacomunicaciones.cl' },
  ];

  const handleCopyEmail = (emailStr: string) => {
    navigator.clipboard.writeText(emailStr);
    setCopiedEmail(emailStr);
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    // Validar teléfono si se proporcionó
    if (formData.phone.trim()) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.isValid) {
        setErrorMessage(phoneValidation.error || 'Por favor ingresa un número de teléfono válido.');
        return;
      }
    }

    setLoading(true);

    try {
      const phoneValid = formData.phone.trim() ? validatePhone(formData.phone) : null;
      const payload = {
        ...formData,
        phone: phoneValid?.formatted || formData.phone,
      };

      // 1. Intento primario: Endpoint Node.js
      let response = await fetch('/api/request-diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      let isJson = response.headers.get('content-type')?.includes('application/json');

      // 2. Si el servidor Node no está disponible (ej. Hosting estático/PHP en Hostinger que devuelve 404 o HTML)
      if (!response.ok || !isJson) {
        response = await fetch('/api/send-email.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        isJson = response.headers.get('content-type')?.includes('application/json');
      }

      if (response.ok && isJson) {
        const data = await response.json();
        if (data.success) {
          setSubmittedRef(data.refCode || 'EP-2026');
          return;
        } else {
          setErrorMessage(data.error || 'No se pudo enviar el correo automático.');
        }
      } else {
        setErrorMessage('No se pudo procesar la solicitud en el servidor. Contáctanos vía info@epicacomunicaciones.cl');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Error de conexión con el servidor. Contáctanos vía info@epicacomunicaciones.cl');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-16 md:py-28 bg-[#f8f7f4] dark:bg-[#070e17] text-white relative transition-colors duration-300 overflow-hidden">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Main Card Container */}
        <div className="relative rounded-2xl sm:rounded-3xl bg-[#0c2738] dark:bg-slate-900 border border-slate-800 dark:border-slate-800/80 p-4 sm:p-8 lg:p-12 shadow-2xl overflow-hidden max-w-full">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column: Headline & Details */}
            <div className="lg:col-span-6 space-y-5 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-cyan-500 to-indigo-500 p-0.5 shadow-md">
                <div className="w-full h-full bg-slate-950 rounded-[10px] flex items-center justify-center font-black text-cyan-400 text-lg">
                  é
                </div>
              </div>

              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight break-words">
                Agendemos una <br className="hidden sm:inline" />
                reunión <br className="hidden sm:inline" />
                estratégica
              </h2>

              <p className="text-xs sm:text-base text-slate-300 leading-relaxed break-words">
                Cuéntanos tu desafío de comunicación y diseñaremos juntos una estrategia a la medida de tu organización.
              </p>

              {/* 3 Official Email Mailboxes */}
              <div className="space-y-3 pt-2 min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-widest text-cyan-400">
                  Casillas Oficiales de Correo Directo
                </p>

                <div className="space-y-2 min-w-0">
                  {officialEmails.map((item) => (
                    <div
                      key={item.email}
                      className="p-3 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between gap-2.5 text-xs w-full min-w-0"
                    >
                      <div className="min-w-0 flex items-center gap-2.5 flex-1">
                        <Mail className="w-4 h-4 text-cyan-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">{item.label}</p>
                          <a
                            href={`mailto:${item.email}`}
                            className="font-mono text-[11px] sm:text-xs text-white hover:text-cyan-300 transition-colors break-all truncate block max-w-full"
                          >
                            {item.email}
                          </a>
                        </div>
                      </div>

                      <button
                        onClick={() => handleCopyEmail(item.email)}
                        title="Copiar correo"
                        className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white transition-colors shrink-0"
                      >
                        {copiedEmail === item.email ? (
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Phone, WhatsApp & Address */}
              <div className="space-y-3 pt-3 text-xs text-slate-300 border-t border-slate-800 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <Phone className="w-4 h-4 text-cyan-400 shrink-0" />
                      <a href={`tel:${AGENCY_INFO.phone}`} className="hover:text-cyan-300 transition-colors font-mono text-xs truncate">
                        {AGENCY_INFO.phoneDisplay}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <Phone className="w-4 h-4 text-cyan-400 shrink-0" />
                      <a href={`tel:${AGENCY_INFO.phone2}`} className="hover:text-cyan-300 transition-colors font-mono text-xs truncate">
                        {AGENCY_INFO.phoneDisplay2}
                      </a>
                    </div>
                  </div>

                  <a
                    href={`https://wa.me/${AGENCY_INFO.whatsappClean}?text=${encodeURIComponent('Hola Épica Comunicaciones, me gustaría coordinar una reunión estratégica.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-950/80 border border-emerald-700/60 text-emerald-300 hover:text-white hover:bg-emerald-900 transition-colors text-xs font-bold shrink-0 w-full sm:w-auto mt-1 sm:mt-0"
                  >
                    <MessageCircle className="w-3.5 h-3.5 text-emerald-400" />
                    <span>WhatsApp</span>
                  </a>
                </div>

                <div className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
                  <span className="truncate">{AGENCY_INFO.address}</span>
                </div>
              </div>
            </div>

            {/* Right Column: Form Container */}
            <div className="lg:col-span-6 bg-slate-900/90 backdrop-blur-md border border-slate-700/60 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl w-full min-w-0">
              {submittedRef ? (
                <div className="text-center py-6 sm:py-8 space-y-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/10">
                    <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight">¡Solicitud Enviada!</h3>
                  <p className="text-xs sm:text-sm text-slate-300 leading-relaxed max-w-md mx-auto">
                    Gracias <span className="font-bold text-white">{formData.name}</span>. ÉPICA Comunicaciones revisará tus datos y te contactará en breve.
                  </p>
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSubmittedRef(null);
                        setFormData({
                          name: '',
                          company: '',
                          email: '',
                          phone: '',
                          message: '',
                        });
                      }}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-cyan-500/20 active:scale-95"
                    >
                      <span>Ingresar nueva solicitud</span>
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3.5">
                  {errorMessage && (
                    <div className="p-3.5 rounded-xl bg-rose-950/80 border border-rose-500/50 text-rose-200 text-xs leading-relaxed">
                      <p className="font-bold text-rose-300 mb-1">⚠️ Error al enviar solicitud:</p>
                      <p>{errorMessage}</p>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-bold text-slate-300 uppercase mb-1">Nombre</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Tu nombre"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-400"
                      />
                    </div>

                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-bold text-slate-300 uppercase mb-1">Empresa</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Tu organización"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-400"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <div>
                      <label className="block text-[10px] sm:text-[11px] font-bold text-slate-300 uppercase mb-1">Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="tu@correo.cl"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-400"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-[10px] sm:text-[11px] font-bold text-slate-300 uppercase">
                          Teléfono (Celular / Fijo)
                        </label>
                        {(() => {
                          if (!formData.phone.trim()) return null;
                          const res = validatePhone(formData.phone);
                          if (!res.isValid) {
                            return <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full border bg-rose-500/20 text-rose-300 border-rose-500/30">⚠️ Inválido</span>;
                          }
                          if (res.type === 'celular') {
                            return <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-500/20 text-emerald-300 border-emerald-500/30">📱 Celular Válido</span>;
                          }
                          if (res.type === 'fijo') {
                            return <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full border bg-cyan-500/20 text-cyan-300 border-cyan-500/30">☎️ Red Fija Válida</span>;
                          }
                          return <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full border bg-purple-500/20 text-purple-300 border-purple-500/30">🌐 Internacional</span>;
                        })()}
                      </div>
                      <div className="flex items-center rounded-xl bg-slate-950 border border-slate-700 overflow-hidden focus-within:border-cyan-400 transition-colors">
                        <div className="bg-slate-900 border-r border-slate-800 px-3 py-2.5 flex items-center gap-2 select-none shrink-0">
                          <span className="inline-flex items-center justify-center font-extrabold text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 leading-none">
                            CL
                          </span>
                          <span className="text-xs font-bold text-cyan-400 leading-none flex items-center">+56</span>
                        </div>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="9 8765 4321 ó 2 2123 4567"
                          className="w-full px-3 py-2.5 bg-transparent text-white text-xs focus:outline-none placeholder:text-slate-600"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] sm:text-[11px] font-bold text-slate-300 uppercase mb-1">Mensaje</label>
                    <textarea
                      rows={4}
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Cuéntanos tu desafío"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:outline-none focus:border-cyan-400"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 px-6 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-lg active:scale-95"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    <span>Enviar solicitud</span>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Multi-colored Accent Line */}
        <div className="mt-8 h-1 w-full rounded-full bg-gradient-to-r from-blue-500 via-emerald-500 via-rose-500 to-purple-500" />
      </div>
    </section>
  );
}
